import { useMemo } from 'react';

import Text from '@cartwhl/ui/base/Text';

import type {
  BoneAssignmentDiagram,
  BoneAssignmentDiagramNode,
  BoneAssignmentDiagramChainNodeMarker,
  BoneAssignmentDiagramChainOverlaySegment,
  BoneAssignmentRoleState,
} from '../types';
import { buildConnectionKey, formatAssignmentLabel } from '../utils';
import DiagramRoleNode from './DiagramRoleNode';

interface BoneAssignmentDiagramBaseProps {
  diagram: BoneAssignmentDiagram;
  assignedRoleNames?: ReadonlySet<string>;
}

interface BoneAssignmentDiagramRolesProps extends BoneAssignmentDiagramBaseProps {
  mode: 'roles';
  roleAssignments: ReadonlyMap<string, BoneAssignmentRoleState>;
  focusedRoleName: string | null;
  pendingRoleName: string | null;
  selectedBoneRef: string | null;
  assignmentEnabled: boolean;
  formatRoleLabel: (roleName: string) => string;
  onRoleClick: (roleName: string) => void;
  onHoveredRoleChange: (roleName: string | null) => void;
  onRoleClear?: (roleName: string) => void;
}

interface BoneAssignmentDiagramChainsProps extends BoneAssignmentDiagramBaseProps {
  mode: 'chains';
  chainSegments: readonly BoneAssignmentDiagramChainOverlaySegment[];
  chainNodeMarkers: readonly BoneAssignmentDiagramChainNodeMarker[];
  hoveredChainName: string | null;
  onHoveredChainChange: (chainName: string | null) => void;
  onChainClick: (chainName: string) => void;
}

type BoneAssignmentDiagramProps =
  | BoneAssignmentDiagramRolesProps
  | BoneAssignmentDiagramChainsProps;

type DiagramChainDetails = {
  startRole: string;
  endRole: string;
  startBoneName: string | null;
  endBoneName: string | null;
  color: string;
};

type DiagramChainEndpointLabelData = {
  key: string;
  node: BoneAssignmentDiagramNode;
  endpointLabel: string;
  boneName: string;
  color: string;
};

const ASSIGNED_CHAIN_OPACITY = 0.96;
const EMPHASIZED_CHAIN_OPACITY = 0.98;
const HOVERED_CHAIN_OPACITY = 1;
const UNASSIGNED_CHAIN_OPACITY = 0.18;
const HOVERED_CHAIN_STROKE_WIDTH = 4;
const EMPHASIZED_CHAIN_STROKE_WIDTH = 3.2;
const ASSIGNED_CHAIN_STROKE_WIDTH = 2.6;
const UNASSIGNED_CHAIN_STROKE_WIDTH = 2.2;
const BODY_DIAGRAM_KEY = 'body';
const BODY_DIAGRAM_CENTER_LINE_X = 50;
const BODY_HOCK_CENTER_SHIFT = 5;
const BODY_HOCK_ROLE_NAMES = new Set(['LeftHock', 'RightHock']);

export default function BoneAssignmentDiagram({
  diagram,
  ...modeProps
}: BoneAssignmentDiagramProps) {
  const renderNodes = useMemo(
    () =>
      adjustAssignedBodyHockNodes(
        normalizeDiagramNodes(diagram.nodes, diagram.contentBounds),
        diagram.key,
        modeProps.assignedRoleNames,
      ),
    [diagram.contentBounds, diagram.key, diagram.nodes, modeProps.assignedRoleNames],
  );
  const nodeByRole = useMemo(
    () => new Map(renderNodes.map((node) => [node.roleName, node])),
    [renderNodes],
  );
  const connectionByKey = useMemo(
    () =>
      new Map(
        diagram.connections.map((connection) => [
          buildConnectionKey(connection.from, connection.to),
          connection,
        ]),
      ),
    [diagram.connections],
  );
  const resolvedConnections = useMemo(
    () =>
      diagram.connections.flatMap((connection) => {
        const fromNode = nodeByRole.get(connection.from);
        const toNode = nodeByRole.get(connection.to);
        if (!fromNode || !toNode) return [];
        return [{ fromNode, toNode }];
      }),
    [diagram.connections, nodeByRole],
  );
  const resolvedChainSegments = useMemo(() => {
    if (modeProps.mode !== 'chains') return [];
    return modeProps.chainSegments.flatMap((segment) => {
      const connection = connectionByKey.get(
        buildConnectionKey(segment.from, segment.to),
      );
      if (!connection) return [];
      const fromNode = nodeByRole.get(connection.from);
      const toNode = nodeByRole.get(connection.to);
      if (!fromNode || !toNode) return [];
      return [{ segment, fromNode, toNode }];
    });
  }, [connectionByKey, modeProps, nodeByRole]);
  const resolvedChainNodeMarkers = useMemo(() => {
    if (modeProps.mode !== 'chains') return [];
    return modeProps.chainNodeMarkers.flatMap((marker) => {
      const node = nodeByRole.get(marker.roleName);
      if (!node) return [];
      return [{ marker, node }];
    });
  }, [modeProps, nodeByRole]);
  const chainDetailsByName = useMemo(() => {
    const details = new Map<string, DiagramChainDetails>();
    if (modeProps.mode !== 'chains') return details;

    for (const segment of modeProps.chainSegments) {
      if (details.has(segment.chainName)) continue;
      details.set(segment.chainName, {
        startRole: segment.startRole,
        endRole: segment.endRole,
        startBoneName: segment.startBoneName,
        endBoneName: segment.endBoneName,
        color: segment.color,
      });
    }

    for (const marker of modeProps.chainNodeMarkers) {
      if (details.has(marker.chainName)) continue;
      details.set(marker.chainName, {
        startRole: marker.startRole,
        endRole: marker.endRole,
        startBoneName: marker.startBoneName,
        endBoneName: marker.endBoneName,
        color: marker.color,
      });
    }

    return details;
  }, [modeProps]);
  const hoveredChainEndpointLabels = useMemo(() => {
    if (modeProps.mode !== 'chains' || !modeProps.hoveredChainName) return [];

    const chainDetails = chainDetailsByName.get(modeProps.hoveredChainName);
    if (!chainDetails) return [];

    const labels: DiagramChainEndpointLabelData[] = [];
    const startNode = nodeByRole.get(chainDetails.startRole);
    const endNode = nodeByRole.get(chainDetails.endRole);
    if (
      startNode &&
      endNode &&
      chainDetails.startRole === chainDetails.endRole &&
      chainDetails.startBoneName &&
      chainDetails.startBoneName === chainDetails.endBoneName
    ) {
      labels.push({
        key: `${modeProps.hoveredChainName}-shared`,
        node: startNode,
        endpointLabel: 'Start / End',
        boneName: chainDetails.startBoneName,
        color: chainDetails.color,
      });
      return labels;
    }

    if (startNode && chainDetails.startBoneName) {
      labels.push({
        key: `${modeProps.hoveredChainName}-start`,
        node: startNode,
        endpointLabel: 'Start',
        boneName: chainDetails.startBoneName,
        color: chainDetails.color,
      });
    }

    if (endNode && chainDetails.endBoneName) {
      labels.push({
        key: `${modeProps.hoveredChainName}-end`,
        node: endNode,
        endpointLabel: 'End',
        boneName: chainDetails.endBoneName,
        color: chainDetails.color,
      });
    }

    return labels;
  }, [chainDetailsByName, modeProps, nodeByRole]);

  return (
    <div className="rounded-xl border border-white/15 bg-white/10 p-1 dark:border-gray-60/35 dark:bg-gray-75/10">
      <Text variant="studio/body/medium/sm" className="text-primary block">
        {diagram.label}
      </Text>
      <div
        className="relative mx-auto w-full"
        style={{
          aspectRatio: `${diagram.maxWidthPx} / ${diagram.minHeightPx}`,
          maxWidth: `${diagram.maxWidthPx}px`,
        }}
        onPointerLeave={
          modeProps.mode === 'chains'
            ? () => modeProps.onHoveredChainChange(null)
            : undefined
        }
      >
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full text-gray-45 dark:text-gray-45"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden
        >
          {resolvedConnections.map(({ fromNode, toNode }) => (
            <line
              key={`${fromNode.roleName}-${toNode.roleName}`}
              x1={fromNode.x}
              y1={fromNode.y}
              x2={toNode.x}
              y2={toNode.y}
              stroke="currentColor"
              strokeWidth={
                fromNode.size === 'xs' && toNode.size === 'xs' ? 0.9 : 1.3
              }
              strokeLinecap="round"
            />
          ))}
        </svg>

        {modeProps.mode === 'roles' ? (
          renderNodes.map((node) => {
            const assignment = modeProps.roleAssignments.get(node.roleName) ?? {
              boneRef: null,
              boneName: null,
              confidence: null,
            };
            const isPending = modeProps.pendingRoleName === node.roleName;
            const isActive = modeProps.focusedRoleName === node.roleName;
            const isAssignedToSelectedBone =
              modeProps.selectedBoneRef !== null &&
              assignment.boneRef === modeProps.selectedBoneRef;

            return (
              <DiagramRoleNode
                key={node.roleName}
                fullLabel={modeProps.formatRoleLabel(node.roleName)}
                boneName={assignment.boneName}
                confidence={assignment.confidence}
                x={node.x}
                y={node.y}
                size={node.size}
                isActive={isActive}
                isPending={isPending}
                isAssignedToSelectedBone={isAssignedToSelectedBone}
                assignmentEnabled={modeProps.assignmentEnabled}
                roleName={node.roleName}
                onClick={() => modeProps.onRoleClick(node.roleName)}
                onHoverChange={modeProps.onHoveredRoleChange}
                onClear={
                  assignment.boneRef && modeProps.onRoleClear
                    ? () => modeProps.onRoleClear?.(node.roleName)
                    : undefined
                }
              />
            );
          })
        ) : (
          <>
            <svg
              className="absolute inset-0 h-full w-full"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              aria-hidden
            >
              {resolvedChainSegments.map(({ segment, fromNode, toNode }) => {
                const isHovered = modeProps.hoveredChainName === segment.chainName;
                const isVisuallyFocused = segment.isFocused && segment.isAssigned;
                const strokeWidth =
                  isHovered
                    ? HOVERED_CHAIN_STROKE_WIDTH
                    : isVisuallyFocused || segment.isPending
                      ? EMPHASIZED_CHAIN_STROKE_WIDTH
                      : segment.isAssigned
                        ? ASSIGNED_CHAIN_STROKE_WIDTH
                        : UNASSIGNED_CHAIN_STROKE_WIDTH;
                const strokeOpacity = segment.isPending
                  ? HOVERED_CHAIN_OPACITY
                  : isHovered
                      ? HOVERED_CHAIN_OPACITY
                      : isVisuallyFocused
                        ? EMPHASIZED_CHAIN_OPACITY
                        : segment.isAssigned
                          ? ASSIGNED_CHAIN_OPACITY
                          : UNASSIGNED_CHAIN_OPACITY;

                return (
                  <g key={`${segment.chainName}-${fromNode.roleName}-${toNode.roleName}`}>
                    <line
                      data-chain-name={segment.chainName}
                      x1={fromNode.x}
                      y1={fromNode.y}
                      x2={toNode.x}
                      y2={toNode.y}
                      stroke="transparent"
                      strokeWidth={10}
                      strokeLinecap="round"
                      style={{ pointerEvents: 'stroke' }}
                      onPointerEnter={(event) => {
                        event.stopPropagation();
                        modeProps.onHoveredChainChange(segment.chainName);
                      }}
                      onPointerLeave={(event) => {
                        event.stopPropagation();
                        if (isPointerWithinSameChain(event.relatedTarget, segment.chainName)) {
                          return;
                        }
                        modeProps.onHoveredChainChange(null);
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                        modeProps.onChainClick(segment.chainName);
                      }}
                    />
                    <line
                      x1={fromNode.x}
                      y1={fromNode.y}
                      x2={toNode.x}
                      y2={toNode.y}
                      stroke={segment.color}
                      strokeWidth={strokeWidth}
                      strokeLinecap="round"
                      strokeDasharray={
                        !segment.isAssigned && segment.isPending
                          ? '5 4'
                          : undefined
                      }
                      opacity={strokeOpacity}
                      style={{ pointerEvents: 'none' }}
                    />
                  </g>
                );
              })}
            </svg>

            {renderNodes.map((node) => (
              <DiagramChainAnchorNode
                key={node.roleName}
                x={node.x}
                y={node.y}
              />
            ))}

            {resolvedChainNodeMarkers.map(({ marker, node }) => (
              <DiagramChainNodeMarker
                key={`${marker.chainName}-${marker.roleName}`}
                chainName={marker.chainName}
                x={node.x}
                y={node.y}
                color={marker.color}
                isAssigned={marker.isAssigned}
                isFocused={marker.isFocused}
                isPending={marker.isPending}
                isHovered={modeProps.hoveredChainName === marker.chainName}
                onHoverChange={modeProps.onHoveredChainChange}
                onClick={() => modeProps.onChainClick(marker.chainName)}
              />
            ))}

            {hoveredChainEndpointLabels.map(({ key, node, endpointLabel, boneName, color }) => (
              <DiagramChainEndpointLabel
                key={key}
                x={node.x}
                y={node.y}
                endpointLabel={endpointLabel}
                boneName={boneName}
                color={color}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

interface DiagramChainAnchorNodeProps {
  x: number;
  y: number;
}

function DiagramChainAnchorNode({ x, y }: DiagramChainAnchorNodeProps) {
  return (
    <span
      className="pointer-events-none absolute inline-flex h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/20 bg-white/70 dark:border-gray-60/25 dark:bg-gray-45/70"
      style={{ left: `${x}%`, top: `${y}%` }}
      aria-hidden
    />
  );
}

interface DiagramChainNodeMarkerProps {
  chainName: string;
  x: number;
  y: number;
  color: string;
  isAssigned: boolean;
  isFocused: boolean;
  isPending: boolean;
  isHovered: boolean;
  onHoverChange: (chainName: string | null) => void;
  onClick: () => void;
}

function DiagramChainNodeMarker({
  chainName,
  x,
  y,
  color,
  isAssigned,
  isFocused,
  isPending,
  isHovered,
  onHoverChange,
  onClick,
}: DiagramChainNodeMarkerProps) {
  const isVisuallyFocused = isFocused && isAssigned;
  const sizePx = isHovered ? 22 : isVisuallyFocused || isPending ? 20 : 16;
  const markerOpacity = isPending
    ? HOVERED_CHAIN_OPACITY
    : isHovered
        ? HOVERED_CHAIN_OPACITY
        : isVisuallyFocused
          ? EMPHASIZED_CHAIN_OPACITY
          : isAssigned
            ? 0.96
            : 0.38;

  return (
    <button
      type="button"
      data-chain-name={chainName}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      onPointerEnter={(event) => {
        event.stopPropagation();
        onHoverChange(chainName);
      }}
      onPointerLeave={(event) => {
        event.stopPropagation();
        if (isPointerWithinSameChain(event.relatedTarget, chainName)) return;
        onHoverChange(null);
      }}
      aria-label={`Assign ${formatAssignmentLabel(chainName)} chain`}
      className="absolute inline-flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full shadow-sm transition-transform hover:scale-105"
      style={{
        left: `${x}%`,
        top: `${y}%`,
        width: `${sizePx}px`,
        height: `${sizePx}px`,
        borderColor: color,
        borderStyle: !isAssigned && isPending ? 'dashed' : 'solid',
        borderWidth: isHovered
          ? '2.5px'
          : isVisuallyFocused || isPending
            ? '2px'
            : '1.5px',
        backgroundColor: isAssigned
          ? 'rgba(255, 255, 255, 0.96)'
          : 'rgba(255, 255, 255, 0.78)',
        opacity: markerOpacity,
      }}
    >
      <span
        className="rounded-full"
        style={{
          width: isAssigned ? '7px' : '0px',
          height: isAssigned ? '7px' : '0px',
          backgroundColor: color,
        }}
        aria-hidden
      />
    </button>
  );
}

interface DiagramChainEndpointLabelProps {
  x: number;
  y: number;
  endpointLabel: string;
  boneName: string;
  color: string;
}

function DiagramChainEndpointLabel({
  x,
  y,
  endpointLabel,
  boneName,
  color,
}: DiagramChainEndpointLabelProps) {
  const { transform, textAlign } = getEndpointLabelPlacement(x, y);

  return (
    <div
      className="pointer-events-none absolute z-20"
      style={{
        left: `${x}%`,
        top: `${y}%`,
        transform,
        textAlign,
      }}
    >
      <div
        className="max-w-[164px] rounded-full border border-white/50 px-2 py-1 shadow-sm backdrop-blur-sm"
        style={{ backgroundColor: color }}
      >
        <Text variant="studio/body/medium/xs" className="block truncate text-white">
          {`${endpointLabel}: ${boneName}`}
        </Text>
      </div>
    </div>
  );
}

function getEndpointLabelPlacement(
  x: number,
  y: number,
): { transform: string; textAlign: 'left' | 'center' | 'right' } {
  if (x <= 28) {
    return { transform: 'translate(10px, -50%)', textAlign: 'left' };
  }
  if (x >= 72) {
    return {
      transform: 'translate(calc(-100% - 10px), -50%)',
      textAlign: 'right',
    };
  }
  if (y <= 20) {
    return { transform: 'translate(-50%, 10px)', textAlign: 'center' };
  }
  return {
    transform: 'translate(-50%, calc(-100% - 10px))',
    textAlign: 'center',
  };
}

function isPointerWithinSameChain(
  relatedTarget: EventTarget | null,
  chainName: string,
): boolean {
  return (
    relatedTarget instanceof Element &&
    relatedTarget.getAttribute('data-chain-name') === chainName
  );
}

function normalizeDiagramNodes(
  nodes: readonly BoneAssignmentDiagramNode[],
  contentBounds: BoneAssignmentDiagram['contentBounds'],
): readonly BoneAssignmentDiagramNode[] {
  if (!contentBounds || nodes.length === 0) return nodes;

  const sourceBounds = nodes.reduce(
    (bounds, node) => ({
      left: Math.min(bounds.left, node.x),
      right: Math.max(bounds.right, node.x),
      top: Math.min(bounds.top, node.y),
      bottom: Math.max(bounds.bottom, node.y),
    }),
    {
      left: Number.POSITIVE_INFINITY,
      right: Number.NEGATIVE_INFINITY,
      top: Number.POSITIVE_INFINITY,
      bottom: Number.NEGATIVE_INFINITY,
    },
  );

  return nodes.map((node) => ({
    ...node,
    x: remapValue(
      node.x,
      sourceBounds.left,
      sourceBounds.right,
      contentBounds.left,
      contentBounds.right,
    ),
    y: remapValue(
      node.y,
      sourceBounds.top,
      sourceBounds.bottom,
      contentBounds.top,
      contentBounds.bottom,
    ),
  }));
}

function adjustAssignedBodyHockNodes(
  nodes: readonly BoneAssignmentDiagramNode[],
  diagramKey: string,
  assignedRoleNames?: ReadonlySet<string>,
): readonly BoneAssignmentDiagramNode[] {
  if (
    diagramKey !== BODY_DIAGRAM_KEY ||
    !assignedRoleNames ||
    assignedRoleNames.size === 0
  ) {
    return nodes;
  }

  let didAdjust = false;
  const adjustedNodes = nodes.map((node) => {
    if (
      !BODY_HOCK_ROLE_NAMES.has(node.roleName) ||
      !assignedRoleNames.has(node.roleName)
    ) {
      return node;
    }

    didAdjust = true;
    return {
      ...node,
      x: moveValueToward(node.x, BODY_DIAGRAM_CENTER_LINE_X, BODY_HOCK_CENTER_SHIFT),
    };
  });

  return didAdjust ? adjustedNodes : nodes;
}

function moveValueToward(value: number, target: number, amount: number): number {
  if (value < target) return Math.min(target, value + amount);
  if (value > target) return Math.max(target, value - amount);
  return value;
}

function remapValue(
  value: number,
  sourceMin: number,
  sourceMax: number,
  targetMin: number,
  targetMax: number,
): number {
  if (sourceMax <= sourceMin) {
    return (targetMin + targetMax) / 2;
  }

  const progress = (value - sourceMin) / (sourceMax - sourceMin);
  return targetMin + progress * (targetMax - targetMin);
}
